/**
 * Aba Anexos no modal de lead — visualização dos documentos já enviados
 * no cadastro do cliente ou no contrato (sem upload avulso nesta aba).
 */

import { useQuery } from '@tanstack/react-query';
import { Spin } from 'antd';
import { Download, Eye, FileText } from 'lucide-react';
import { ClientesService } from '@/features/clientes/services/clientes.service';
import { unifiedServices } from '@/services/unifiedService';
import { formatDate } from '@/utils/format';
import { PipelineFormProvider } from '@/components/form/PipelineFormContext';
import {
  PipelineEmbeddedEmpty,
  PipelineGroup,
  PipelineInfoRow,
  PipelineSectionTitle,
} from '@/components/form/pipelineFieldRow';
import { Button } from '@/components/ui/button';

const TIPO_LABELS: Record<string, string> = {
  documento_com_foto: 'Documento com Foto',
  comprovante_residencia: 'Comprovante de Residência',
  comprovante_pagamento: 'Comprovante de Pagamento',
  declaracoes: 'Declarações',
  comprovante_renda: 'Comprovante de Renda',
  geral: 'Anexo',
};

function getTipoLabel(tipo?: string) {
  if (!tipo) return 'Anexo';
  return TIPO_LABELS[tipo] || tipo.replace(/_/g, ' ');
}

interface PipelineLeadAnexosTabProps {
  clienteId: string;
  onGoToCliente?: () => void;
}

export function PipelineLeadAnexosTab({
  clienteId,
  onGoToCliente,
}: PipelineLeadAnexosTabProps) {
  const { data: documentos = [], isLoading } = useQuery({
    queryKey: ['documentos', clienteId],
    queryFn: () => ClientesService.listarDocumentos(clienteId),
    enabled: !!clienteId,
    staleTime: 0,
  });

  const openDocumento = async (doc: any) => {
    const url =
      doc.url ||
      (doc.caminho_arquivo
        ? await unifiedServices.clientes.getDocumentUrl(doc.caminho_arquivo)
        : null);
    if (url) window.open(url, '_blank');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spin />
      </div>
    );
  }

  return (
    <PipelineFormProvider variant="pipeline">
      <div className="px-2 py-2 space-y-3">
        <p className="text-[12px] text-[#8f9aab] px-3 leading-relaxed">
          Anexos enviados no{' '}
          {onGoToCliente ? (
            <button
              type="button"
              onClick={onGoToCliente}
              className="text-[#FF3737] hover:underline"
            >
              cadastro do cliente
            </button>
          ) : (
            'cadastro do cliente'
          )}{' '}
          ou no formulário de contrato.
        </p>

        {documentos.length === 0 ? (
          <PipelineEmbeddedEmpty>
            Nenhum anexo ainda. Adicione na aba Cliente, seção Documentos.
          </PipelineEmbeddedEmpty>
        ) : (
          <div className="space-y-3">
            {documentos.map((doc: any) => {
              const nome = doc.nome_arquivo || 'Arquivo sem nome';
              const tipo = doc.tipo_documento || doc.tipo || 'geral';

              return (
                <div key={doc.id}>
                  <PipelineSectionTitle>{getTipoLabel(tipo)}</PipelineSectionTitle>
                  <PipelineGroup>
                    <PipelineInfoRow label="Arquivo">
                      <span className="flex items-center gap-1.5 truncate">
                        <FileText className="w-3.5 h-3.5 text-[#8f9aab] shrink-0" />
                        <span className="truncate">{nome}</span>
                      </span>
                    </PipelineInfoRow>
                    <PipelineInfoRow
                      label="Enviado em"
                      value={doc.created_at ? formatDate(doc.created_at) : '—'}
                    />
                    <PipelineInfoRow label="Ações">
                      <div className="flex items-center gap-0.5">
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          title="Visualizar"
                          className="h-7 w-7 rounded hover:bg-gray-100 dark:hover:bg-zinc-800"
                          onClick={() => openDocumento(doc)}
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          title="Abrir / download"
                          className="h-7 w-7 rounded hover:bg-gray-100 dark:hover:bg-zinc-800"
                          onClick={() => openDocumento(doc)}
                        >
                          <Download className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </PipelineInfoRow>
                  </PipelineGroup>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </PipelineFormProvider>
  );
}

export default PipelineLeadAnexosTab;
